<template>
    <div class="sidebar-form-configuration">
        <SidebarPanelHeader title="Section Configuration"
                            @save="save(false)"
                            @saveAndClose="save(true)"
                            @close="requestClose" />

        <div :class="styles.FORM.FORM_GROUP">
            <label>Headline</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   :value="sectionConfiguration.headline"
                   @input="handleHeadlineInput($event)">
        </div>

        <div :class="styles.FORM.FORM_GROUP" v-for="lang in enabledTranslationLanguages" :key="'headline-' + lang.code">
            <label>Headline ({{ lang.name }})</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   v-model="sectionConfiguration['headline' + lang.suffix]"
                   :placeholder="lang.name + ' headline'">
            <small v-if="sectionConfiguration['headline' + lang.suffix + 'Manual']" class="form-text text-info">
                <i class="fa fa-pencil"></i> Manually edited
            </small>
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label>Headline Additional Class (CSS)</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   v-model="sectionConfiguration.headlineAdditionalClass">
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label>Sub-Headline</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   v-model="sectionConfiguration.subHeadline">
        </div>

        <div :class="styles.FORM.FORM_GROUP" v-for="lang in enabledTranslationLanguages" :key="'subheadline-' + lang.code">
            <label>Sub-Headline ({{ lang.name }})</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   v-model="sectionConfiguration['subHeadline' + lang.suffix]"
                   :placeholder="lang.name + ' sub-headline'">
            <small v-if="sectionConfiguration['subHeadline' + lang.suffix + 'Manual']" class="form-text text-info">
                <i class="fa fa-pencil"></i> Manually edited
            </small>
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label>Sub-Headline Additional Class (CSS)</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   v-model="sectionConfiguration.subHeadlineAdditionalClass">
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label>
                Show Section Headline?
                <input type="checkbox" v-model="sectionConfiguration.isShowHeadline">
            </label>
        </div>

        <div :class="styles.FORM.FORM_GROUP" data-test="section-break-point">
            <label>
                Break point
                <input type="checkbox" v-model="sectionConfiguration.breakAfter" data-test="section-break-point-checkbox">
            </label>
            <small class="form-text text-muted">
                Patients finish this section, press Save &amp; Continue, and move to the next part.
                Ignored while this section is hidden by a visibility rule. Duplicating a section copies this.
            </small>
        </div>

        <SidebarToggleableContainer
            headline="Visibility / Conditional Logic"
            :siteID="siteID"
        >
            <VisibilityRulesEditor
                :key="'vis-'+fieldsVersion+'-'+(sectionConfiguration.headline||'')"
                :control="sectionConfiguration"
                :formData="formData"
                :availableFields="availableFields"
                @update-rules="onUpdateRules"
            />
        </SidebarToggleableContainer>

        <div class="buttons">
            <button :class="styles.BUTTON.PRIMARY" @click="save(false)">
                Save
            </button>
            <button :class="styles.BUTTON.INFO" @click="save(true)">
                Save & Close
            </button>
        </div>
        <!-- removed accidental code block from template -->
    </div>
</template>

<script>
    import {STYLE_INJECTION_MIXIN} from "../../../mixins/style-injection-mixin";
    import {SIDEBAR_BODY_MIXIN} from "../../../mixins/sidebar-body-mixin";
    import {SECTION_DEFAULT_DATA} from "../../../configs/section";
    import { langSuffix, LANGUAGE_CONFIG } from "../../../utils/languageHelper";

    import SidebarToggleableContainer from "../../../views/container-views/SidebarToggleableContainer";
    import VisibilityRulesEditor from "../control-configuration-views/ControlVisibilityRules";
    import SidebarPanelHeader from "./SidebarPanelHeader";

    export default {
        name: "SidebarSectionConfiguration",
        mixins: [STYLE_INJECTION_MIXIN, SIDEBAR_BODY_MIXIN],
        props: {
            // props injected by GlobalSidebar via <component> rendering
            dataPackage: { type: Object, default: () => ({}) },
            formData: { type: Object, default: () => ({}) },
            permissions: { type: Object, default: () => ({}) },
            siteID: { type: String, default: '' }
        },
        components: {
            SidebarPanelHeader,
            SidebarToggleableContainer,
            VisibilityRulesEditor
        },
        data() {
            return {
                dataKey: "sectionConfiguration",
                sectionConfiguration: Object.assign({}, SECTION_DEFAULT_DATA),
                fieldsVersion: 0
            }
        },
        created() {
            // retrieve the data from `GlobalSidebar` passed in - merge into reactive object
            try {
                Object.assign(this.sectionConfiguration, this.dataPackage)
            } catch (e) {
                // fallback to assignment
                this.sectionConfiguration = Object.assign({}, this.sectionConfiguration, this.dataPackage)
            }
        },
        computed: {
            /**
             * Enabled non-English translation languages for this form
             */
            enabledTranslationLanguages() {
                if (!this.formData || !this.formData.formConfig) return [];
                const supported = this.formData.formConfig.supportedLanguages || [];
                return supported
                    .filter(code => code !== 'en' && LANGUAGE_CONFIG[code])
                    .map(code => ({ code, name: LANGUAGE_CONFIG[code].name, suffix: langSuffix(code) }));
            },

            availableFields() {
                // Build a full list of sections and their fields so dropdown shows ALL sections and ALL their fields
                const fd = this.formData || {}
                // touch these reactive values so computed re-runs when headline or explicit version changes
                const _headline = (this.sectionConfiguration && this.sectionConfiguration.headline) || ''
                const _ver = this.fieldsVersion

                // Helper: normalized section id
                const normSectionId = (s, idx) => s && (s.uniqueId || s.uniqueid || s.id) || `section_${idx}`

                // Prepare section map from fd.sections (preserve order if array)
                const sectionMap = {}
                const sectionOrder = []
                if (fd.sections) {
                    const sectionsArr = Array.isArray(fd.sections) ? fd.sections : Object.values(fd.sections)
                    sectionsArr.forEach((s, i) => {
                        const sid = normSectionId(s, i)
                        const raw = s && (s.headline || s.name) || sid || `Section ${i+1}`
                        const label = s && (s.uniqueId || s.uniqueid) ? `${raw} (${s.uniqueId || s.uniqueid})` : raw
                        sectionMap[sid] = { sectionName: label, fields: [] }
                        sectionOrder.push(sid)
                    })
                }

                // Record current section id for possible reordering later
                const current = this.sectionConfiguration || this.dataPackage || {}
                const currentId = current && (current.uniqueId || current.uniqueid || current.id)
                if (currentId && !sectionMap[currentId]) {
                    const raw = current.headline || current.name || currentId
                    const label = currentId ? `${raw} (${currentId})` : raw
                    // create placeholder; don't force it to the top yet
                    sectionMap[currentId] = { sectionName: label, fields: [] }
                }

                // Iterate through sections and get controls directly from section.controls array
                if (fd.sections) {
                    const sectionsArr = Array.isArray(fd.sections) ? fd.sections : Object.values(fd.sections)
                    sectionsArr.forEach((s, si) => {
                        const sid = normSectionId(s, si)

                        // Controls are stored directly in section.controls as array of control IDs
                        const controlIds = Array.isArray(s.controls) ? s.controls : []
                        controlIds.forEach(controlId => {
                            // Look up the control in fd.controls
                            const ctrl = fd.controls && fd.controls[controlId]
                            if (!ctrl) return
                            // Use control.name if available, otherwise fall back to uniqueId
                            const name = ctrl.name || ctrl.uniqueId || ctrl.uniqueid || controlId
                            if (!sectionMap[sid]) sectionMap[sid] = { sectionName: sid, fields: [] }
                            if (!sectionMap[sid].fields.find(f => f.name === name)) {
                                sectionMap[sid].fields.push({ name, label: ctrl.label || ctrl.name || name, type: ctrl.type })
                            }
                        })

                        // Also check rows if they exist (legacy/alternate structure)
                        const rowIds = Array.isArray(s.rows) ? s.rows : []
                        rowIds.forEach(rowId => {
                            const row = fd.rows && fd.rows[rowId]
                            if (!row) return
                            const rowControls = Array.isArray(row.controls) ? row.controls : []
                            rowControls.forEach(cref => {
                                const ctrl = typeof cref === 'string' ? (fd.controls && fd.controls[cref]) : cref
                                if (!ctrl) return
                                const name = ctrl.name || ctrl.uniqueId || ctrl.uniqueid
                                if (!name) return
                                if (!sectionMap[sid]) sectionMap[sid] = { sectionName: sid, fields: [] }
                                if (!sectionMap[sid].fields.find(f => f.name === name)) {
                                    sectionMap[sid].fields.push({ name, label: ctrl.label || ctrl.name || name, type: ctrl.type })
                                }
                            })
                        })
                    })
                }

                // Build result array preserving order: sectionOrder first.
                // EXCLUDE the current section - a section's visibility can't depend on its own fields
                const result = []
                const pushed = new Set()

                // push sections in original order, but skip the current section
                sectionOrder.forEach(sid => {
                    if (sid === currentId) return // Skip current section
                    if (sectionMap[sid]) { result.push(sectionMap[sid]); pushed.add(sid) }
                })

                // append any remaining sectionMap keys, but skip the current section
                Object.keys(sectionMap).forEach(sid => {
                    if (sid === currentId) return // Skip current section
                    if (!pushed.has(sid)) { result.push(sectionMap[sid]); pushed.add(sid) }
                })

                return result
            }
        },
        watch: {
            'sectionConfiguration.headline': function (newHeadline) {
                // bump version to force availableFields recompute and update child components
                this.fieldsVersion = (this.fieldsVersion || 0) + 1

                // sync headline back to formData.sections if possible (mirror control behavior)
                try {
                    if (!this.formData || !this.sectionConfiguration) return
                    const sid = this.sectionConfiguration.uniqueId || this.sectionConfiguration.uniqueid || this.sectionConfiguration.id
                    if (!sid) return
                    if (this.formData.sections && (this.formData.sections[sid] || Object.values(this.formData.sections).find(s => (s.uniqueId||s.uniqueid||s.id) === sid))) {
                        // if sections is an object map
                        if (this.formData.sections[sid]) {
                            this.$set(this.formData.sections[sid], 'headline', newHeadline)
                        } else {
                            // if sections is an array, find and set
                            const arr = Array.isArray(this.formData.sections) ? this.formData.sections : Object.values(this.formData.sections)
                            const found = arr.find(s => (s.uniqueId||s.uniqueid||s.id) === sid)
                            if (found) this.$set(found, 'headline', newHeadline)
                        }
                    }
                } catch (e) {
                    // ignore
                }
            }
        },
        methods: {
            /**
             * Called by SIDEBAR_BODY_MIXIN.requestClose() once "Discard your unsaved changes?" is confirmed.
             * The headline watcher above mirrors every keystroke into formData.sections (live preview on
             * the canvas), so the headline as of the last Save (or the open) goes back there.
             */
            discardChanges() {
                const clean = this.cleanData()
                const sid = this.sectionConfiguration.uniqueId || this.sectionConfiguration.uniqueid || this.sectionConfiguration.id
                const sections = this.formData && this.formData.sections
                if (!clean || !sid || !sections) {
                    return
                }

                const list = Array.isArray(sections) ? sections : Object.values(sections)
                const found = list.find(s => s && (s.uniqueId || s.uniqueid || s.id) === sid)
                if (found) {
                    this.$set(found, 'headline', clean.headline)
                }
            },

            onUpdateRules(newRules) {
                // update sectionConfiguration.visibilityRules
                this.$set(this.sectionConfiguration, 'visibilityRules', Array.isArray(newRules) ? newRules : [newRules])
            }
            ,
            handleHeadlineInput(e) {
                const v = e && e.target ? e.target.value : ''
                try {
                    this.$set(this.sectionConfiguration, 'headline', v)
                } catch (err) {
                    // fallback
                    this.sectionConfiguration.headline = v
                }
                // force availableFields recompute and remount children
                this.fieldsVersion = (this.fieldsVersion || 0) + 1
            }
        },
    }
</script>